// src/lib/memory.js
//
// Read/write helpers for the landing-v2 memory profile (Stage 1 spec
// PART 10). All reads/writes go through podcaster_profiles — the
// existing user-row table — extended in migration 050 with the memory
// fields. There is intentionally no parallel user_profiles table.
//
// What's here:
//   getMemoryProfile(userId)        → returns the full memory snapshot
//   updateMemoryProfile(userId, patch) → partial update, never destructive
//   recordTopic(userId, topic)      → appends to topics_explored (dedup)
//   recordRecurringTheme(userId, theme)  → appends to recurring_themes (dedup)
//   recordProfanityOk(userId, ok)   → sets profanity_ok once (no overwrite)
//   recordHostNames(userId, names)  → upserts host_names array
//   recordFormatPreference(userId, format)
//   recordContentPreference(userId, pref)
//   adminEmailAutoPromote(email)    → bumps is_admin=true for whitelisted emails
//
// The AI chat layer is expected to call these as side effects of
// conversational turns, not to expose them as commands to the user.

import { supabase } from './supabase';

// Hardcoded admin emails per Stage 1 spec PART 11. Promoted to is_admin
// on first successful signup/login.
// Admin allowlist — only these emails can:
//   - Sign in to /admin and see the AdminPage
//   - Sign in to the main app (everyone else lives on landing-v2)
//   - See the "Admin" button in the landing nav
// Comparison is case-insensitive (lower-cased on both sides in
// isHardcodedAdminEmail). Add new admins here; remove to revoke.
const ADMIN_EMAILS = new Set([
  'drew@smileyhousemedia.com',
  'akaashdesigns@gmail.com',
]);

// Memory-relevant columns we read/write through this module. Listing
// them explicitly so a column rename in the schema is a single point
// of update — and so we don't accidentally pull credit balances or
// pricing fields into the memory layer.
const MEMORY_COLUMNS = [
  'user_id',
  'preferred_name',
  'full_name',
  'communication_style',
  'profanity_ok',
  'show_name',
  'host_names',
  'co_host_names',
  'format_preference',
  'content_preference',
  'niche',
  'audience_size_estimate',
  'platform_mix',
  'topics_explored',
  'recurring_themes',
  'goals',
  'style_notes',
  'personal_context',
];

// ── Read ───────────────────────────────────────────────────────────────

export async function getMemoryProfile(userId) {
  if (!supabase || !userId) return null;
  const { data, error } = await supabase
    .from('podcaster_profiles')
    .select(MEMORY_COLUMNS.join(','))
    .eq('user_id', userId)
    .maybeSingle();
  if (error) {
    console.error('[memory] getMemoryProfile error:', error);
    return null;
  }
  return data || null;
}

// ── Write — generic partial update ─────────────────────────────────────

export async function updateMemoryProfile(userId, patch) {
  if (!supabase || !userId || !patch || typeof patch !== 'object') return null;
  // Strip keys that aren't in MEMORY_COLUMNS so a buggy caller can't
  // accidentally write to plan/credits/etc.
  const safe = {};
  for (const [k, v] of Object.entries(patch)) {
    if (MEMORY_COLUMNS.includes(k) && k !== 'user_id') safe[k] = v;
  }
  if (Object.keys(safe).length === 0) return null;
  safe.updated_at = new Date().toISOString();
  const { data, error } = await supabase
    .from('podcaster_profiles')
    .update(safe)
    .eq('user_id', userId)
    .select(MEMORY_COLUMNS.join(','))
    .maybeSingle();
  if (error) {
    console.error('[memory] updateMemoryProfile error:', error);
    return null;
  }
  return data || null;
}

// ── Write — specialized helpers ────────────────────────────────────────

// Append to topics_explored (postgres array). Dedupes case-insensitively
// against existing entries so re-mentioning a topic doesn't bloat the
// array. Returns the updated array.
export async function recordTopic(userId, topic) {
  if (!userId || !topic) return null;
  const profile = await getMemoryProfile(userId);
  if (!profile) return null;
  const existing = profile.topics_explored || [];
  const lc = String(topic).trim().toLowerCase();
  if (!lc) return existing;
  if (existing.some(t => String(t).toLowerCase() === lc)) return existing;
  const next = [...existing, String(topic).trim()];
  await updateMemoryProfile(userId, { topics_explored: next });
  return next;
}

export async function recordRecurringTheme(userId, theme) {
  if (!userId || !theme) return null;
  const profile = await getMemoryProfile(userId);
  if (!profile) return null;
  const existing = profile.recurring_themes || [];
  const lc = String(theme).trim().toLowerCase();
  if (!lc) return existing;
  if (existing.some(t => String(t).toLowerCase() === lc)) return existing;
  const next = [...existing, String(theme).trim()];
  await updateMemoryProfile(userId, { recurring_themes: next });
  return next;
}

// First-write-wins. Calling with true/false sets the field; calling
// when already set is a no-op. Lets the AI "ask once" without risk of
// the answer flipping on a misread later in the conversation.
export async function recordProfanityOk(userId, ok) {
  if (!userId || typeof ok !== 'boolean') return null;
  const profile = await getMemoryProfile(userId);
  if (!profile) return null;
  if (profile.profanity_ok === true || profile.profanity_ok === false) {
    return profile.profanity_ok; // already set, no-op
  }
  await updateMemoryProfile(userId, { profanity_ok: ok });
  return ok;
}

export async function recordHostNames(userId, names) {
  if (!userId || !Array.isArray(names)) return null;
  const cleaned = names.map(n => String(n).trim()).filter(Boolean);
  return updateMemoryProfile(userId, { host_names: cleaned });
}

export async function recordCoHostNames(userId, names) {
  if (!userId || !Array.isArray(names)) return null;
  const cleaned = names.map(n => String(n).trim()).filter(Boolean);
  return updateMemoryProfile(userId, { co_host_names: cleaned });
}

export async function recordFormatPreference(userId, format) {
  if (!userId || !['solo', 'interview', 'co_hosted'].includes(format)) return null;
  return updateMemoryProfile(userId, { format_preference: format });
}

export async function recordContentPreference(userId, pref) {
  if (!userId || !['script', 'talking_points'].includes(pref)) return null;
  return updateMemoryProfile(userId, { content_preference: pref });
}

export async function recordCommunicationStyle(userId, style) {
  if (!userId || !['casual', 'formal', 'dry', 'warm', 'mixed'].includes(style)) return null;
  return updateMemoryProfile(userId, { communication_style: style });
}

// personal_context is jsonb — write organic mentions ("user's daughter Sarah")
// as additive keys without clobbering existing context. The AI layer is
// responsible for deciding what to write here; this helper just merges.
export async function recordPersonalContext(userId, partial) {
  if (!userId || !partial || typeof partial !== 'object') return null;
  const profile = await getMemoryProfile(userId);
  if (!profile) return null;
  const merged = { ...(profile.personal_context || {}), ...partial };
  return updateMemoryProfile(userId, { personal_context: merged });
}

// ── Admin auto-promote ─────────────────────────────────────────────────

// Hardcoded admin emails (Stage 1 spec PART 11). Called on first
// successful signup/login. Idempotent — safe to call repeatedly.
// Returns true if a promotion happened, false otherwise.
export async function adminEmailAutoPromote(userId, email) {
  if (!supabase || !userId || !email) return false;
  const lc = String(email).trim().toLowerCase();
  if (!ADMIN_EMAILS.has(lc)) return false;
  const { error } = await supabase
    .from('podcaster_profiles')
    .update({ is_admin: true, updated_at: new Date().toISOString() })
    .eq('user_id', userId);
  if (error) {
    console.error('[memory] adminEmailAutoPromote error:', error);
    return false;
  }
  return true;
}

export function isHardcodedAdminEmail(email) {
  if (!email) return false;
  return ADMIN_EMAILS.has(String(email).trim().toLowerCase());
}

// ── Returning-user openers (Stage 1 spec PART 9, build-page rotating hero) ──
//
// Kept here because they read from the memory profile — `preferred_name`
// or `full_name`. Picking the opener is deterministic-per-day so the
// same user sees the same line for a given session, not a new opener
// on every render.
export function pickReturningOpener(profile) {
  const name = (profile?.preferred_name || profile?.full_name || '').trim().split(/\s+/)[0] || '';
  const lines = [
    `Hey ${name || 'there'}, what episode are we creating today?`.trim(),
    `Welcome back, ${name || 'there'} — let's make your best episode yet.`.trim(),
    `What episode are we building today?`,
    `Let's talk about something cool today.`,
    `Ready to make your best episode yet?`,
    `Welcome back. What's on your mind?`,
    `Got an idea brewing?`,
  ];
  const idx = Math.floor(Math.random() * lines.length);
  return lines[idx];
}
